/**
 * What's New popup state.
 *
 * Checked once per app load (see App.jsx), right after sign-in. The server
 * has already decided WHETHER to show it (a fix-only batch never re-opens it
 * for someone who dismissed already) and WHAT to show (every entry, features
 * and fixes, added since the last dismissal) — this store just holds that
 * answer and the open/closed state of the modal.
 */

import { create } from 'zustand';
import { acknowledgeWhatsNew, getWhatsNewStatus } from '@/api/endpoints/whatsNew.js';

export const useWhatsNewStore = create((set, get) => ({
  open: false,
  entries: [],
  checked: false,

  /** Fetch once per session; opens the modal only if the server says to. */
  async checkOnLogin() {
    if (get().checked) return;
    set({ checked: true });
    try {
      const res = await getWhatsNewStatus();
      if (res?.shouldShow && Array.isArray(res.entries) && res.entries.length > 0) {
        set({ entries: res.entries, open: true });
      }
    } catch {
      // Never block sign-in on this — silently skip the popup for this session.
    }
  },

  /**
   * Close the modal. When `dontShowAgain` is true this also persists the
   * dismissal server-side, so a future FEATURE is what re-opens it — a
   * fix-only release never will. Leaving it unticked closes the modal for
   * this session only; it reappears with the same content next sign-in.
   */
  async dismiss(dontShowAgain) {
    set({ open: false });
    try {
      await acknowledgeWhatsNew(!!dontShowAgain);
    } catch {
      // Best-effort — worst case it asks again next time.
    }
  }
}));
